import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract (see the framing addendum): the light here is the
// buffer's own history; a rig would only pollute the loop.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js). requiresTier: the
// ping-pong pass pair does not fit tier 0's budget, so the orchestrator must
// never offer Echo there - enforced in orchestrator.js against
// opts.qualityTier, not here.
export const PROFILE = {
  id: 'echo',
  family: 'shader',
  danceability: 0.80,
  // 0, not 1. requiresTier gates SELECTION, not capability: this file has
  // had a real tier-0 layout all along (see its TIER table). The radio page
  // settles at tier 0 on the hardware it actually runs on - measured on
  // judicael.com - so at 1 this was never offered there at all, which is
  // why none of the recent visualizers ever appeared on the web radio. The
  // cost is that weak devices now get it at its tier-0 layout rather than
  // not at all; the frame-time governor still owns the tier itself.
  requiresTier: 0,
  energyLevel: 0.80,
  bpmMin: 115,
  bpmMax: 180,
  movement: 0.80,
  tonality: 0.55,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// Echo: a video feedback loop - the previous frame fed back into the next
// with zoom, rotation and hue drift. Camera pointed at its own monitor, the
// oldest trick in the VJ book. The whole art is in the STABILITY: a feedback
// loop diverges the moment its effective gain crosses 1, so three cumulative
// guards (decay < 1, a constant floor subtraction, a hard ceiling) plus an
// automatic governor watching the buffer's real mean luminance make
// divergence impossible even against a mis-set knob.
//
// THE RENDER PASSES run inside the display quad's onBeforeRender, which is
// the one place a party-mode visualizer can legally reach the WebGLRenderer
// (visualizers are handed scene+camera only; scene-manager owns the
// renderer). Three.js keeps a render-state stack precisely so a texture can
// be rendered on demand right before the mesh that samples it draws - the
// same pattern CubeCamera uses. The previous render target is saved and
// restored around the passes, because at that moment the composer's OWN
// RenderPass target is bound, not the default framebuffer.

// --- the loop ---------------------------------------------------------------
// 0.955: at 0.94 a trail died in about half a second and the frame read as
// empty between beats - the governor guards the top end, so the default can
// afford to sit high.
const DECAY_DEFAULT = 0.955;
const DECAY_MIN = 0.85;
const DECAY_MAX = 0.975;      // never >= 1, whatever the governor does
const FLOOR_SUB = 0.004;      // weak pixels die instead of pooling into grey
const CEILING = 0.92;         // bloom catches injected content, never the loop
// Governor cadence and thresholds - see _measureLuminance.
const GOVERN_EVERY_FRAMES = 30;
const GOVERN_HIGH = 0.35;
const GOVERN_RESUME = 0.25;
const GOVERN_LOW = 0.05;
const GOVERN_STEP = 0.01;
const LUM_SIZE = 8;

// --- the five transforms ----------------------------------------------------
// zoom > 1 pulls history TOWARD the centre (the sampled UV moves outward).
const MODES = {
  tunnel: { zoom: 1.03, rotation: 0, offset: [0, 0], mirror: 0 },
  bloom: { zoom: 0.97, rotation: 0, offset: [0, 0], mirror: 0 },
  spiral: { zoom: 1.015, rotation: 0.012, offset: [0, 0], mirror: 0 },
  mirror: { zoom: 1.02, rotation: 0, offset: [0, 0], mirror: 1 },
  drift: { zoom: 1.0, rotation: 0, offset: [0.004, 0.002], mirror: 0 }
};
const MODE_TRANSITION_BEATS = 2;   // interpolated, never cut - a cut jumps
const ZOOM_BAND_SWING = 0.015;     // bands.low, around the mode's own zoom
const BAND_TAU = 0.15;             // the 100ms-binned envelope needs smoothing
const HUE_SHIFT_MIN = 0.002;
const HUE_SHIFT_MAX = 0.008;
// SPIRAL: one full revolution per 16 beats when the bpm is known.
const SPIRAL_BEATS_PER_REV = 16;

// --- injection --------------------------------------------------------------
const ART_SIZE = 0.22;         // of buffer height, centred
const ART_OPACITY = 0.16;
const SHAPE_LIFE_S = 0.12;     // brief - the trail is the loop's job
// 3 frames, not 6: the flash re-injects every frame it lives, and the loop
// STACKS those copies - at 0.10s/0.10 opacity the "very attenuated" pulse
// measured 0.5 mean luminance on a dark buffer.
const FLASH_LIFE_S = 0.05;
const FLASH_OPACITY = 0.06;    // very attenuated full-frame pulse
const TRACK_FADE_S = 1.2;      // track change: fade to black, never a clear
// Decay during that fade. NOT 0.85: the floor subtraction (0.004/frame) rides
// on top of the multiplier, and together they killed the buffer in ~10
// frames - a cut in all but name. 0.96^72 plus the floor lands at black just
// as the 1.2s window closes.
const FADE_DECAY = 0.96;

const TIER = {
  // Tier 0 exists only as a live-downgrade refuge: the orchestrator never
  // OFFERS echo below tier 1 (requiresTier), but the frame-time governor can
  // drop the tier mid-composition, and the tier-aware rebuild then lands
  // here. Quarter resolution, cheapest modes, until the next swap retires it.
  0: { scale: 0.25, modes: ['tunnel', 'bloom', 'drift'], hueShift: false },
  1: { scale: 0.25, modes: ['tunnel', 'bloom', 'drift'], hueShift: false },
  2: { scale: 0.5, modes: ['tunnel', 'bloom', 'spiral', 'mirror', 'drift'], hueShift: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// Hue rotation as an RGB matrix (rotation about the grey axis) - an HSL
// round-trip per pixel would cost more than the whole feedback shader.
function hueMatrix(angle, out) {
  const c = Math.cos(angle), s = Math.sin(angle);
  const a = 1 / 3, b = Math.sqrt(1 / 3);
  out.set(
    c + (1 - c) * a, a * (1 - c) - b * s, a * (1 - c) + b * s,
    a * (1 - c) + b * s, c + (1 - c) * a, a * (1 - c) - b * s,
    a * (1 - c) - b * s, a * (1 - c) + b * s, c + (1 - c) * a
  );
  return out;
}

const feedbackVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const feedbackFragmentShader = `
  precision mediump float;

  uniform sampler2D uPrevious;
  uniform float uZoom;
  uniform float uRotation;   // radians per frame, applied once per pass
  uniform vec2 uOffset;
  uniform float uMirrorAmt;
  uniform float uDecay;
  uniform mat3 uHueMat;
  uniform float uSeed;

  varying vec2 vUv;

  void main() {
    vec2 p = vUv - 0.5;
    // MIRROR interpolates through a mix on the folded coordinate - abs() is
    // not itself interpolable, but a blend toward the folded position is,
    // which is what keeps criterion "no jump on mode change" true for the
    // kaleidoscopic mode too.
    p.x = mix(p.x, abs(p.x), uMirrorAmt);
    float c = cos(uRotation), s = sin(uRotation);
    p = vec2(p.x * c - p.y * s, p.x * s + p.y * c);
    p *= uZoom;
    p += uOffset;
    vec3 prev = texture2D(uPrevious, p + 0.5).rgb;

    // Hue drift: the trails change colour as they age.
    prev = uHueMat * prev;

    // STABILITY - all three guards, cumulative (see the file header):
    prev *= uDecay;                 // gain strictly below 1
    prev -= vec3(${FLOOR_SUB.toFixed(4)});   // weak pixels extinguish
    prev = max(prev, 0.0);
    prev = min(prev, vec3(${CEILING.toFixed(3)}));   // ceiling under bloom's threshold

    // A feedback loop recirculates its own quantisation error and bands
    // within seconds without this. +-0.5/255, fp16-safe hash (wrapped
    // coordinate - see the TV note in lava.js).
    vec2 fcw = mod(floor(gl_FragCoord.xy) + uSeed, 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    // MASKED ON BLACK. An unsigned target truncates the dither's negative
    // half, leaving a net +0.5/510 per pass on empty pixels - which balanced
    // against the decay into a permanent grey veil over the whole frame
    // (measured ~0.02 linear, i.e. the exact failure the floor subtraction
    // exists to prevent, reintroduced by the anti-banding fix). Banding only
    // exists where there is signal, so the dither only applies there.
    // 3.5/255, not the quantisation step: right at the threshold the dither
    // keeps a stochastic population of near-black pixels alive (a visible
    // crawling texture in the background). Below this the floor subtraction
    // kills a pixel in two frames and the background settles to true black;
    // trail gradients live far above it, so the anti-banding is untouched.
    float sig = step(3.5 / 255.0, max(prev.r, max(prev.g, prev.b)));

    gl_FragColor = vec4(prev + dith * sig, 1.0);
  }
`;

// The injected shapes are ANALYTIC, drawn on plain quads by these two
// shaders rather than tessellated geometry.
//
// The cause of the coarse circles was ALIASING, not faceting - worth writing
// down because the obvious suspect is wrong. A 64-segment RingGeometry has
// 18px chords at this radius, which sounds coarse, but its radial error is
// only R*(1-cos(pi/64)) = 0.2px: invisible. What was visible is that the
// chain runs antialias:false with no MSAA anywhere (see scene-manager), the
// feedback buffer is HALF resolution, and it is then upscaled 2x to the
// display - so every hard geometric edge became a 2-screen-pixel staircase,
// which the loop then recirculated and compounded into the trails.
//
// An SDF edge ramped over a known fraction of a buffer pixel survives that
// upscale, and is exactly circular at any resolution as a bonus.
//
// The edge softness is a UNIFORM computed on the CPU from the real buffer
// height (uPx = one buffer pixel, in ortho units), not fwidth(): a GLSL ES
// 1.00 shader needs GL_OES_standard_derivatives explicitly enabled and this
// codebase deliberately avoids depending on it (see splitflap.js). Deriving
// it from a size we already know is exact and costs nothing.
const shapeVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const ringFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec2 uHalfSize;   // quad half-extents, ortho units
  uniform float uInner;     // ring radii, ortho units
  uniform float uOuter;
  uniform float uPx;        // one buffer pixel, ortho units
  varying vec2 vUv;
  void main() {
    float r = length((vUv - 0.5) * 2.0 * uHalfSize);
    float w = max(uPx * 1.5, 1e-5);
    float a = clamp((uOuter - r) / w, 0.0, 1.0) * clamp((r - uInner) / w, 0.0, 1.0);
    // AdditiveBlending is (SRC_ALPHA, ONE): the whole intensity lives in rgb
    // and alpha stays 1, or it would be squared.
    gl_FragColor = vec4(uColor * (a * uOpacity), 1.0);
  }
`;

const barFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec2 uHalfSize;   // quad half-extents, ortho units
  uniform vec2 uRect;       // the BAR's own half-extents, inset from the quad
  uniform float uPx;
  varying vec2 vUv;
  void main() {
    vec2 p = abs((vUv - 0.5) * 2.0 * uHalfSize);
    float w = max(uPx * 1.5, 1e-5);
    // The quad is larger than the bar so the feather lives in the margin and
    // the bar keeps its full width - fading the quad's own edges inward would
    // eat 20% of a 14px bar.
    float a = clamp((uRect.x - p.x) / w, 0.0, 1.0) * clamp((uRect.y - p.y) / w, 0.0, 1.0);
    gl_FragColor = vec4(uColor * (a * uOpacity), 1.0);
  }
`;

const displayVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uBuffer;
  varying vec2 vUv;
  void main() {
    gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0);
  }
`;

// Downsample for the governor: one quad sampling the buffer into an 8x8
// target, read back as 64 pixels. LinearFilter makes each of the 64 texels a
// small regional average - sparse but plenty for a mean-luminance estimate.
const lumFragmentShader = `
  precision mediump float;
  uniform sampler2D uBuffer;
  varying vec2 vUv;
  void main() {
    gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0);
  }
`;

export class EchoVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    const forcedMode = urlParam('echomode');
    this._forcedMode = forcedMode && MODES[forcedMode] ? forcedMode : null;
    const forcedDecay = parseFloat(urlParam('echodecay'));
    this._forcedDecay = Number.isFinite(forcedDecay) ? forcedDecay : null;
    this._lumDebug = urlParam('echolum') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    const layout = TIER[this._tier];
    this._layout = layout;

    this._buffer = new FeedbackBuffer({ scale: layout.scale });

    // --- feedback pass ---
    this._feedbackUniforms = {
      uPrevious: { value: null },
      uZoom: { value: 1.0 },
      uRotation: { value: 0 },
      uOffset: { value: new THREE.Vector2(0, 0) },
      uMirrorAmt: { value: 0 },
      uDecay: { value: this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT },
      uHueMat: { value: hueMatrix(0, new THREE.Matrix3()) },
      uSeed: { value: 0 }
    };
    this._feedbackScene = new THREE.Scene();
    this._feedbackQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._feedbackUniforms,
        vertexShader: feedbackVertexShader,
        fragmentShader: feedbackFragmentShader,
        depthTest: false,
        depthWrite: false
      })
    );
    this._feedbackQuad.frustumCulled = false;
    this._feedbackScene.add(this._feedbackQuad);

    // --- injection pass (drawn into the same target, no clear) ---
    this._injectScene = new THREE.Scene();
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;

    this._artTexture = null;
    this._artMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: ART_OPACITY, depthTest: false, depthWrite: false })
    );
    this._artMesh.scale.set(ART_SIZE, ART_SIZE, 1);
    this._artMesh.visible = false;
    this._injectScene.add(this._artMesh);

    // The beat shapes: a ring, a bar, a burst (two crossed bars) -
    // pre-allocated, recycled by visibility. All analytic (see the shaders).
    this._shapeMaterials = [];
    this._ring = this._makeRing(0.26, 0.30, 1.0);
    this._bar = this._makeBar(0.55, 0.02);
    this._burstA = this._makeBar(0.35, 0.0175);
    this._burstB = this._makeBar(0.0175, 0.35);
    this._shapes = [this._ring, this._bar, this._burstA, this._burstB];
    for (const m of this._shapes) { m.visible = false; this._injectScene.add(m); }

    // A RING, not a full-frame plane. A uniform plane fed through the zoom
    // stays a uniform plane - the "concentric wave" the bar flash is meant to
    // leave came out as a full-frame grey throb (measured: the whole corner
    // at 75/255 for ~0.7s after every bar). A wide ring contracts through
    // TUNNEL and expands through BLOOM, which is the wave as intended.
    this._flashMesh = this._makeRing(0.42, 0.60, FLASH_OPACITY);
    this._flashMesh.visible = false;
    this._injectScene.add(this._flashMesh);

    // --- governor downsample target ---
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_SIZE, LUM_SIZE, {
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      depthBuffer: false, stencilBuffer: false
    });
    this._lumScene = new THREE.Scene();
    this._lumUniforms = { uBuffer: { value: null } };
    this._lumQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._lumUniforms,
        vertexShader: displayVertexShader,
        fragmentShader: lumFragmentShader,
        depthTest: false, depthWrite: false
      })
    );
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._lumPixels = new Uint8Array(LUM_SIZE * LUM_SIZE * 4);

    // --- display quad in the MAIN scene, before the shared bloom ---
    this._displayUniforms = { uBuffer: { value: null } };
    this._displayMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._displayUniforms,
        vertexShader: displayVertexShader,
        fragmentShader: displayFragmentShader,
        depthTest: false,
        depthWrite: false
      })
    );
    this._displayMesh.frustumCulled = false;
    this._displayMesh.renderOrder = -1000;
    const self = this;
    this._displayMesh.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._displayMesh);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._decay = this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT;
    this._decayCeiling = Math.max(this._decay, DECAY_DEFAULT);
    this._governorHot = false;
    this._frameCount = 0;
    this._lastLum = 0;
    this._modeIndex = 0;
    this._modeName = this._forcedMode || layout.modes[0];
    const m = MODES[this._modeName];
    this._current = { zoom: m.zoom, rotation: m.rotation, ox: m.offset[0], oy: m.offset[1], mirror: m.mirror };
    this._from = Object.assign({}, this._current);
    this._target = Object.assign({}, this._current);
    this._transitionT = 1;
    this._transitionDur = 1;
    this._lastPhraseIndex = null;
    this._lastBeatIndex = null;
    this._lastBarIndex = null;
    this._lowSmooth = 0.5;
    this._highSmooth = 0.5;
    this._hueAngle = 0;
    this._hueDir = 1;
    this._palette = [new THREE.Color(0x39a0ff), new THREE.Color(0xff3ba7), new THREE.Color(0xffd23b)];
    this._lastPaletteRef = undefined;
    this._lastTrackId = undefined;
    this._lastArtworkUrl = undefined;
    // Cold start: the loop begins from a black buffer, and until the beat
    // grid arrives (the first poll takes seconds) nothing injects but the
    // faint artwork - the screen reads as dead. Until the first REAL beat
    // fires, shapes self-seed on a metronome so the feedback has something
    // to chew on from frame one.
    this._coldStart = true;
    this._nextSeedAt = 0;
    this._fadeUntil = -1;
    this._fading = false;
    this._shapeUntil = -1;
    this._flashUntil = -1;
    this._bpm = null;

    if (this._lumDebug) {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;left:10px;top:10px;z-index:99999;'
        + 'font:12px/1.5 ui-monospace,monospace;color:#9fe;background:rgba(0,0,0,.65);'
        + 'padding:6px 9px;border-radius:6px;white-space:pre';
      document.body.appendChild(el);
      this._lumEl = el;
    }
  }

  // A ring on a quad big enough to hold its own feather.
  _makeRing(inner, outer, opacity) {
    const half = outer * 1.15;
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0xffffff) },
        uOpacity: { value: opacity },
        uHalfSize: { value: new THREE.Vector2(half, half) },
        uInner: { value: inner },
        uOuter: { value: outer },
        uPx: { value: 1 / 360 }
      },
      vertexShader: shapeVertexShader,
      fragmentShader: ringFragmentShader,
      blending: THREE.AdditiveBlending,
      transparent: true, depthTest: false, depthWrite: false
    });
    this._shapeMaterials.push(mat);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(half * 2, half * 2), mat);
    mesh.frustumCulled = false;
    return mesh;
  }

  // A bar, inset in a quad that carries the feather margin.
  _makeBar(halfX, halfY) {
    const m = 0.02;   // margin, ortho units - comfortably over one pixel
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0xffffff) },
        uOpacity: { value: 1 },
        uHalfSize: { value: new THREE.Vector2(halfX + m, halfY + m) },
        uRect: { value: new THREE.Vector2(halfX, halfY) },
        uPx: { value: 1 / 360 }
      },
      vertexShader: shapeVertexShader,
      fragmentShader: barFragmentShader,
      blending: THREE.AdditiveBlending,
      transparent: true, depthTest: false, depthWrite: false
    });
    this._shapeMaterials.push(mat);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry((halfX + m) * 2, (halfY + m) * 2), mat);
    mesh.frustumCulled = false;
    return mesh;
  }

  // The mean luminance of the freshly written buffer, via the 8x8 downsample
  // + readback. Called every GOVERN_EVERY_FRAMES frames from inside the pass
  // block (the render target is already ours). An 8x8 readback is 256 bytes;
  // the cost that matters is the pipeline flush it forces, which is why this
  // is on a cadence and not per frame.
  _measureLuminance(renderer) {
    this._lumUniforms.uBuffer.value = this._buffer.write.texture;
    renderer.setRenderTarget(this._lumTarget);
    renderer.render(this._lumScene, this._ortho);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_SIZE, LUM_SIZE, this._lumPixels);
    let sum = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) {
      sum += 0.299 * this._lumPixels[i] + 0.587 * this._lumPixels[i + 1] + 0.114 * this._lumPixels[i + 2];
    }
    const lum = sum / (255 * LUM_SIZE * LUM_SIZE);
    this._lastLum = lum;

    // The governor. Hysteresis: once over GOVERN_HIGH it keeps stepping the
    // decay down each measurement until the mean is back under GOVERN_RESUME,
    // so one hot measurement cannot flap. This is what makes divergence
    // impossible even with ?echodecay=0.99.
    if (lum > GOVERN_HIGH) this._governorHot = true;
    if (this._governorHot) {
      if (lum > GOVERN_RESUME) {
        const before = this._decay;
        this._decay = Math.max(DECAY_MIN, this._decay - GOVERN_STEP);
        if (this._decay !== before) {
          // eslint-disable-next-line no-console
          console.log('[echo] governor: mean luminance ' + lum.toFixed(3)
            + ' -> decay ' + before.toFixed(3) + ' -> ' + this._decay.toFixed(3));
        }
      } else {
        this._governorHot = false;
      }
    } else if (lum < GOVERN_LOW && this._decay < Math.min(this._decayCeiling, DECAY_MAX)) {
      const before = this._decay;
      this._decay = Math.min(Math.min(this._decayCeiling, DECAY_MAX), this._decay + GOVERN_STEP);
      // eslint-disable-next-line no-console
      console.log('[echo] governor: mean luminance ' + lum.toFixed(3)
        + ' -> decay ' + before.toFixed(3) + ' -> ' + this._decay.toFixed(3));
    }
  }

  // All the render-target work, run from the display quad's onBeforeRender -
  // see the file header for why this is the legal place.
  _runPasses(renderer) {
    const rebuilt = this._buffer.ensureSize(renderer);
    if (rebuilt) {
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      // The injection camera matches the buffer's aspect (height = 1), so a
      // ring is round and the artwork square on a 16:9 buffer.
      const aspect = this._buffer.width / Math.max(1, this._buffer.height);
      this._ortho.left = -0.5 * aspect;
      this._ortho.right = 0.5 * aspect;
      this._ortho.updateProjectionMatrix();
      // One buffer pixel in ortho units - the shapes' edge softness. The
      // ortho camera spans 1.0 vertically over the buffer's height, so this
      // is exact at every tier and after every resize.
      const px = 1 / Math.max(1, this._buffer.height);
      for (const mat of this._shapeMaterials) mat.uniforms.uPx.value = px;
      this._flashMesh.scale.set(1, 1, 1);   // the ring stays round
    }
    if (this._paused) {
      // Frozen: no feedback pass at all, the display quad re-shows the last
      // buffer untouched.
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      return;
    }

    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;

    // 1. feedback: read -> write, transformed + guarded + dithered
    this._feedbackUniforms.uPrevious.value = this._buffer.read.texture;
    // The track-change fade rides on decay INSIDE the pass block - written
    // here, not in update(), because this line runs after it every frame and
    // would silently override it. 0.85^72 frames over 1.2s reaches black.
    this._feedbackUniforms.uDecay.value = this._fading ? FADE_DECAY : this._decay;
    this._feedbackUniforms.uSeed.value = (this._frameCount * 13.13) % 64;
    renderer.setRenderTarget(this._buffer.write);
    renderer.autoClear = false;   // the feedback quad overwrites every pixel
    renderer.render(this._feedbackScene, this._ortho);

    // 2. injection, into the same target, on top
    renderer.render(this._injectScene, this._ortho);

    // 3. governor cadence
    this._frameCount++;
    // The governor sleeps through a track fade: it would read the fading
    // buffer as "too dark", push decay to its ceiling, and hand the incoming
    // track an over-hot loop - measured as a 0.54 mean-luminance spike on
    // the first beats after the fade.
    if (!this._fading && this._frameCount % GOVERN_EVERY_FRAMES === 0) this._measureLuminance(renderer);

    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;

    // 4. swap; the freshly written frame is what the main scene displays
    this._buffer.swap();
    this._displayUniforms.uBuffer.value = this._buffer.read.texture;

    if (this._lumEl && this._frameCount % 10 === 0) {
      this._lumEl.textContent = 'echo  lum ' + this._lastLum.toFixed(3)
        + '\n      decay ' + this._decay.toFixed(3)
        + '\n      mode ' + this._modeName + (this._governorHot ? '\n      GOVERNOR ACTIVE' : '');
    }
  }

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : [0x39a0ff, 0xff3ba7, 0xffd23b];
    for (let i = 0; i < 3; i++) this._palette[i].set(vivid[i % vivid.length]);
    // The palette also decides which way the trails drift around the wheel.
    const hsl = { h: 0, s: 0, l: 0 };
    this._palette[0].getHSL(hsl);
    this._hueDir = hsl.h >= 0.5 ? -1 : 1;
  }

  _startTransition(name, bpm) {
    if (!MODES[name]) return;
    this._modeName = name;
    // From the APPLIED values, not the stored ones: SPIRAL's rotation tracks
    // the bpm and the zoom breathes with bands.low, so the stored mode values
    // can sit away from what the shader actually ran last frame - starting
    // there would be exactly the buffer jump criterion 4 forbids.
    this._from = {
      zoom: this._feedbackUniforms.uZoom.value,
      rotation: this._feedbackUniforms.uRotation.value,
      ox: this._feedbackUniforms.uOffset.value.x,
      oy: this._feedbackUniforms.uOffset.value.y,
      mirror: this._feedbackUniforms.uMirrorAmt.value
    };
    const m = MODES[name];
    this._target = { zoom: m.zoom, rotation: m.rotation, ox: m.offset[0], oy: m.offset[1], mirror: m.mirror };
    this._transitionT = 0;
    const spb = bpm ? 60 / bpm : 0.5;
    this._transitionDur = Math.max(0.2, MODE_TRANSITION_BEATS * spb);
  }

  _spawnShape() {
    const kind = Math.floor(Math.random() * 3);
    const color = this._palette[Math.floor(Math.random() * 3)];
    const angle = Math.random() * Math.PI * 2;
    let meshes;
    if (kind === 0) meshes = [this._ring];
    else if (kind === 1) meshes = [this._bar];
    else meshes = [this._burstA, this._burstB];
    for (const m of meshes) {
      m.visible = true;
      m.rotation.z = angle;
      m.material.uniforms.uColor.value.copy(color);
      m.material.uniforms.uOpacity.value = 1.0;
    }
    this._shapeUntil = this._time + SHAPE_LIFE_S;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    this._time += deltaTime;

    const director = profile.director;
    const bands = director && director.bands;
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-deltaTime / BAND_TAU);
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || null;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }

    // Track change: 1.2s fade of the whole buffer toward black - never a
    // clear. During the fade nothing is injected, so the incoming track
    // genuinely starts from darkness.
    if (profile.trackId !== this._lastTrackId) {
      const first = this._lastTrackId === undefined;
      this._lastTrackId = profile.trackId;
      if (!first) this._fadeUntil = this._time + TRACK_FADE_S;
    }
    const fading = this._time < this._fadeUntil;
    this._fading = fading;

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: false },
          function (tex) {
            if (self._artTexture) self._artTexture.dispose();
            self._artTexture = tex;
            self._artMesh.material.map = tex;
            self._artMesh.material.needsUpdate = true;
            self._artMesh.visible = true;
          },
          function () {});
      } else {
        this._artMesh.visible = false;
      }
    }

    // --- musical events ---
    const beatIndex = profile.beatIndex;
    if (this._coldStart && !fading && this._time >= this._nextSeedAt) {
      this._nextSeedAt = this._time + 0.7;
      this._spawnShape();
    }
    if (beatIndex != null && this._lastBeatIndex != null && beatIndex !== this._lastBeatIndex && !fading) {
      this._coldStart = false;
      this._spawnShape();
      // Bar downbeat: the concentric flash.
      const barIndex = profile.barIndex;
      if (barIndex != null && barIndex !== this._lastBarIndex) {
        this._flashMesh.visible = true;
        this._flashUntil = this._time + FLASH_LIFE_S;
      }
      this._lastBarIndex = profile.barIndex;
    } else if (this._lastBarIndex === null) {
      this._lastBarIndex = profile.barIndex;
    }
    this._lastBeatIndex = beatIndex;

    const phraseIndex = profile.phraseIndex;
    if (this._forcedMode == null
      && phraseIndex != null && this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex) {
      const modes = this._layout.modes;
      this._modeIndex = (this._modeIndex + 1) % modes.length;
      this._startTransition(modes[this._modeIndex], this._bpm);
    }
    this._lastPhraseIndex = phraseIndex;

    // Retire expired injected content.
    if (this._time >= this._shapeUntil) for (const m of this._shapes) m.visible = false;
    if (this._time >= this._flashUntil) this._flashMesh.visible = false;
    this._artMesh.visible = !!this._artTexture && !fading;
    if (fading) { for (const m of this._shapes) m.visible = false; this._flashMesh.visible = false; }

    // --- transform interpolation ---
    if (this._transitionT < 1) {
      this._transitionT = Math.min(1, this._transitionT + deltaTime / this._transitionDur);
    }
    const t = this._transitionT;
    const cur = this._current;
    cur.zoom = lerp(this._from.zoom, this._target.zoom, t);
    cur.rotation = lerp(this._from.rotation, this._target.rotation, t);
    cur.ox = lerp(this._from.ox, this._target.ox, t);
    cur.oy = lerp(this._from.oy, this._target.oy, t);
    cur.mirror = lerp(this._from.mirror, this._target.mirror, t);

    // bands.low breathes the zoom around the mode's own value.
    let zoom = cur.zoom + (this._lowSmooth - 0.5) * 2 * ZOOM_BAND_SWING;
    // SPIRAL follows the tempo: one revolution per 16 beats.
    let rotPerFrame = cur.rotation;
    if (this._modeName === 'spiral' && this._bpm) {
      const framesPerRev = SPIRAL_BEATS_PER_REV * (60 / this._bpm) * 60;
      rotPerFrame = (Math.PI * 2) / framesPerRev * (cur.rotation >= 0 ? 1 : -1);
      rotPerFrame = lerp(this._from.rotation, rotPerFrame, t);
    }

    this._feedbackUniforms.uZoom.value = zoom;
    this._feedbackUniforms.uRotation.value = rotPerFrame;
    this._feedbackUniforms.uOffset.value.set(cur.ox, cur.oy);
    this._feedbackUniforms.uMirrorAmt.value = cur.mirror;

    // Hue drift, tier 2 only.
    if (this._layout.hueShift) {
      const shift = lerp(HUE_SHIFT_MIN, HUE_SHIFT_MAX, this._highSmooth) * this._hueDir;
      this._hueAngle = shift * Math.PI * 2;
      hueMatrix(this._hueAngle, this._feedbackUniforms.uHueMat.value);
    }
  }

  dispose() {
    this._scene.remove(this._displayMesh);
    this._displayMesh.geometry.dispose();
    this._displayMesh.material.dispose();
    this._feedbackQuad.geometry.dispose();
    this._feedbackQuad.material.dispose();
    for (const m of this._shapes.concat([this._artMesh, this._flashMesh])) {
      m.geometry.dispose();
      m.material.dispose();
    }
    this._shapeMaterials.length = 0;
    this._lumQuad.geometry.dispose();
    this._lumQuad.material.dispose();
    this._lumTarget.dispose();
    this._buffer.dispose();
    if (this._artTexture) this._artTexture.dispose();
    if (this._lumEl && this._lumEl.parentNode) this._lumEl.parentNode.removeChild(this._lumEl);
  }
}
